// The 5-year funding-period clock. A client is eligible again once the
// designated funding period has elapsed since their last ADP-funded fitting;
// no prior claim on record means eligible now. Dates are ISO yyyy-mm-dd worked
// in UTC so there is no timezone slippage, and adding the period clamps a
// Feb-29 anniversary down to Feb-28 in a non-leap target year.

import { FUNDING_PERIOD_YEARS, type AdpEligibility, type FittingType } from "./types";

const isLeap = (y: number): boolean => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const pad = (n: number): string => String(n).padStart(2, "0");

export function addYears(iso: string, years: number): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const targetYear = y + years;
  const day = m === 2 && d === 29 && !isLeap(targetYear) ? 28 : d;
  return `${targetYear}-${pad(m)}-${pad(day)}`;
}

function daysBetween(fromIso: string, toIso: string): number {
  const ms = Date.parse(toIso + "T00:00:00Z") - Date.parse(fromIso + "T00:00:00Z");
  return Math.round(ms / 86_400_000);
}

export function fundingPeriodClock(
  fittingType: FittingType,
  priorFundingDate: string | null | undefined,
  asOf: string,
): AdpEligibility {
  const fundingPeriodYears = FUNDING_PERIOD_YEARS[fittingType];
  const prior = priorFundingDate ?? null;
  if (!prior) {
    return { fundingPeriodYears, priorFundingDate: null, eligibleFrom: null, asOf, eligible: true, daysRemaining: 0 };
  }
  const eligibleFrom = addYears(prior, fundingPeriodYears);
  const remaining = daysBetween(asOf, eligibleFrom);
  return {
    fundingPeriodYears,
    priorFundingDate: prior,
    eligibleFrom,
    asOf,
    eligible: remaining <= 0,
    daysRemaining: Math.max(0, remaining),
  };
}
